import { storeToRefs } from "pinia";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import type { Interview } from "@/types";
import type { GridChoice, GridColumnOption } from "@stores/survey/grids/gridRows";

export type InterviewColumn = Pick<GridColumnOption, "question_name"> & Partial<Pick<GridColumnOption, "choices">>;

interface ColumnValue {
  values?: string[];
  ai_justification?: { message_id?: string | number };
}

interface Message {
  id: string | number;
  content: string;
  translated_content?: string[];
}

const columnData = (interview: Interview, column: InterviewColumn) =>
  (interview as unknown as Record<string, ColumnValue | undefined>)[column.question_name];

export const useInterviewValues = () => {
  const { currentLocale } = storeToRefs(useSurveyLocalesStore());

  const localizeChoice = (value: string, choices: Record<string, GridChoice>) => {
    const choice = Object.values(choices).find((c) => c.title === value);
    return choice?.localized_title?.[currentLocale.value?.token as string] || choice?.title || value;
  };

  const columnValues = (interview: Interview, column: InterviewColumn): string[] => {
    const values = columnData(interview, column)?.values ?? [];
    if (!currentLocale.value?.token || !column.choices) return values;
    return values.map((value) => localizeChoice(value, column.choices as Record<string, GridChoice>));
  };

  const columnValue = (interview: Interview, column: InterviewColumn) => columnValues(interview, column).join(", ");

  const justificationMessageId = (interview: Interview, column: InterviewColumn) =>
    columnData(interview, column)?.ai_justification?.message_id ?? null;

  const translatedQuote = (interview: Interview, messageId: string | number | null) => {
    if (!messageId) return null;
    const message = (interview.conversation as Message[] | undefined)?.find((m) => m.id === messageId);
    if (!message) return null;
    const sameLanguage = interview.language === currentLocale.value?.language;
    return sameLanguage ? message.content : message.translated_content?.[0] || message.content;
  };

  return { columnValues, columnValue, justificationMessageId, translatedQuote };
};
